import { getRegistrations } from 'src/services/arlo/arlo-service';
import { RegistrationItem } from 'src/types';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  PAGE_SIZE,
  syncBookings,
  SyncMode,
  syncPageOneInPlace,
} from './syncBookings';

export interface Pagination {
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
  nextPage: number | null;
  previousPage: number | null;
}

export interface RefetchOptions {
  silent?: boolean;
}

type RegistrationStatus = 'Pending' | 'Approved' | 'Rejected';

interface ApplyPageOptions {
  mode: SyncMode;
  preserveTrailingPages?: boolean;
}

interface BookingsState {
  organisationId: string | null;
  pending: RegistrationItem[];
  approved: RegistrationItem[];
  rejected: RegistrationItem[];
  pendingPagination: Pagination | null;
  approvedPagination: Pagination | null;
  rejectedPagination: Pagination | null;
  pendingFullyLoaded: boolean;
  approvedFullyLoaded: boolean;
  rejectedFullyLoaded: boolean;
  pendingBackgroundInFlight: boolean;
  approvedBackgroundInFlight: boolean;
  rejectedBackgroundInFlight: boolean;
  pendingTotalCount: number | null;
  approvedTotalCount: number | null;
  rejectedTotalCount: number | null;
  lastSyncedAt: string | null;
  isLoading: boolean;
  isRefetching: boolean;
  error: string | null;
  hasLoadedOnce: boolean;
  isFetching: boolean;
  isChildOrganisation: boolean | null;
  reset: () => void;
  applyPageResult: (
    status: RegistrationStatus,
    items: RegistrationItem[],
    pagination: Pagination | null,
    options: ApplyPageOptions
  ) => void;
  fetchRemainingPagesInBackground: (
    status: RegistrationStatus
  ) => Promise<void>;
  refetch: (
    status?: RegistrationStatus,
    options?: RefetchOptions
  ) => Promise<void>;
}

const initialState = {
  organisationId: null as string | null,
  pending: [] as RegistrationItem[],
  approved: [] as RegistrationItem[],
  rejected: [] as RegistrationItem[],
  pendingPagination: null as Pagination | null,
  approvedPagination: null as Pagination | null,
  rejectedPagination: null as Pagination | null,
  pendingFullyLoaded: false,
  approvedFullyLoaded: false,
  rejectedFullyLoaded: false,
  pendingBackgroundInFlight: false,
  approvedBackgroundInFlight: false,
  rejectedBackgroundInFlight: false,
  pendingTotalCount: null as number | null,
  approvedTotalCount: null as number | null,
  rejectedTotalCount: null as number | null,
  lastSyncedAt: null as string | null,
  isLoading: false,
  isRefetching: false,
  error: null as string | null,
  hasLoadedOnce: false,
  isFetching: false,
  isChildOrganisation: null as boolean | null,
};

const getStatusState = (
  state: BookingsState,
  status: RegistrationStatus
): {
  items: RegistrationItem[];
  pagination: Pagination | null;
  fullyLoaded: boolean;
  backgroundInFlight: boolean;
  itemsKey: 'pending' | 'approved' | 'rejected';
  paginationKey:
    | 'pendingPagination'
    | 'approvedPagination'
    | 'rejectedPagination';
  fullyLoadedKey:
    | 'pendingFullyLoaded'
    | 'approvedFullyLoaded'
    | 'rejectedFullyLoaded';
  backgroundInFlightKey:
    | 'pendingBackgroundInFlight'
    | 'approvedBackgroundInFlight'
    | 'rejectedBackgroundInFlight';
  totalCountKey:
    | 'pendingTotalCount'
    | 'approvedTotalCount'
    | 'rejectedTotalCount';
} =>
  status === 'Pending'
    ? {
        items: state.pending,
        pagination: state.pendingPagination,
        fullyLoaded: state.pendingFullyLoaded,
        backgroundInFlight: state.pendingBackgroundInFlight,
        itemsKey: 'pending',
        paginationKey: 'pendingPagination',
        fullyLoadedKey: 'pendingFullyLoaded',
        backgroundInFlightKey: 'pendingBackgroundInFlight',
        totalCountKey: 'pendingTotalCount',
      }
    : status === 'Approved'
    ? {
        items: state.approved,
        pagination: state.approvedPagination,
        fullyLoaded: state.approvedFullyLoaded,
        backgroundInFlight: state.approvedBackgroundInFlight,
        itemsKey: 'approved',
        paginationKey: 'approvedPagination',
        fullyLoadedKey: 'approvedFullyLoaded',
        backgroundInFlightKey: 'approvedBackgroundInFlight',
        totalCountKey: 'approvedTotalCount',
      }
    : {
        items: state.rejected,
        pagination: state.rejectedPagination,
        fullyLoaded: state.rejectedFullyLoaded,
        backgroundInFlight: state.rejectedBackgroundInFlight,
        itemsKey: 'rejected',
        paginationKey: 'rejectedPagination',
        fullyLoadedKey: 'rejectedFullyLoaded',
        backgroundInFlightKey: 'rejectedBackgroundInFlight',
        totalCountKey: 'rejectedTotalCount',
      };

const setStatusTotalCount = (
  set: (partial: Partial<BookingsState>) => void,
  status: RegistrationStatus,
  totalCount: number
): void => {
  const totalCountKey =
    status === 'Pending'
      ? 'pendingTotalCount'
      : status === 'Approved'
      ? 'approvedTotalCount'
      : 'rejectedTotalCount';
  set({ [totalCountKey]: totalCount } as Partial<BookingsState>);
};

const getNextPage = (
  pagination: Pagination | null | undefined
): number | null => {
  if (!pagination?.hasNext) return null;
  if (pagination.nextPage != null) return pagination.nextPage;
  return pagination.currentPage + 1;
};

const isFullyLoadedForStatus = (
  state: BookingsState,
  status: RegistrationStatus,
  hasMorePages: boolean
): boolean => {
  if (hasMorePages) return false;

  const statusState = getStatusState(state, status);
  const totalCount = state[statusState.totalCountKey];

  return totalCount == null || statusState.items.length >= totalCount;
};

export const useBookingsStore = create<BookingsState>()(
  persist(
    (set, get) => ({
      ...initialState,

      reset: () => set({ ...initialState }),

      applyPageResult: (status, items, pagination, options) => {
        const state = get();
        const statusState = getStatusState(state, status);
        const { next, hasChanges } = options.preserveTrailingPages
          ? syncPageOneInPlace(statusState.items, items)
          : syncBookings(statusState.items, items, { mode: options.mode });

        if (!hasChanges && pagination === statusState.pagination) {
          return;
        }

        set({
          [statusState.itemsKey]: next,
          [statusState.paginationKey]: pagination,
          ...(hasChanges ? { lastSyncedAt: new Date().toISOString() } : {}),
        } as Partial<BookingsState>);
      },

      fetchRemainingPagesInBackground: async (status) => {
        const state = get();
        const organisationId = state.organisationId;
        if (!organisationId) return;

        const statusState = getStatusState(state, status);
        if (statusState.backgroundInFlight) return;

        set({
          [statusState.backgroundInFlightKey]: true,
        } as Partial<BookingsState>);

        try {
          let page = getNextPage(statusState.pagination) ?? 2;
          let hasMorePages = true;

          while (page && hasMorePages) {
            const data = await getRegistrations(
              organisationId,
              status,
              page,
              PAGE_SIZE
            );

            get().applyPageResult(
              status,
              data.approvals,
              data.pagination ?? null,
              { mode: 'append' }
            );

            setStatusTotalCount(set, status, data.totalCount);

            const statusAfterApply = getStatusState(get(), status);
            const totalCount = get()[statusAfterApply.totalCountKey];
            if (
              totalCount != null &&
              statusAfterApply.items.length >= totalCount
            ) {
              hasMorePages = false;
              break;
            }

            const nextPage = getNextPage(data.pagination);
            if (!nextPage) {
              hasMorePages = false;
              break;
            }

            page = nextPage;
          }

          set({
            [statusState.fullyLoadedKey]: isFullyLoadedForStatus(
              get(),
              status,
              hasMorePages
            ),
            [statusState.backgroundInFlightKey]: false,
          } as Partial<BookingsState>);
        } catch {
          set({
            [statusState.backgroundInFlightKey]: false,
          } as Partial<BookingsState>);
        }
      },

      refetch: async (status, options) => {
        const organisationId = get().organisationId;
        if (!organisationId) return;

        const isSilent = options?.silent === true;
        if (isSilent && get().isFetching) return;

        const isInitialLoad = !get().hasLoadedOnce;
        if (isInitialLoad) {
          set({ isLoading: true });
        } else if (!isSilent) {
          set({ isRefetching: true });
        }
        set({ error: null, isFetching: true });

        const fetchStatusPageOne = async (targetStatus: RegistrationStatus) => {
          const data = await getRegistrations(
            organisationId,
            targetStatus,
            1,
            PAGE_SIZE
          );
          const statusState = getStatusState(get(), targetStatus);
          const preserveTrailingPages =
            isSilent && statusState.items.length > PAGE_SIZE;

          get().applyPageResult(
            targetStatus,
            data.approvals,
            data.pagination ?? null,
            {
              mode: 'replace',
              preserveTrailingPages,
            }
          );

          setStatusTotalCount(set, targetStatus, data.totalCount);

          if (
            targetStatus === 'Pending' &&
            data.isChildOrganisation !== undefined
          ) {
            set({ isChildOrganisation: data.isChildOrganisation ?? null });
          }

          if (data.pagination?.hasNext) {
            if (statusState.fullyLoaded) {
              set({
                [statusState.fullyLoadedKey]: false,
              } as Partial<BookingsState>);
            }
            void get().fetchRemainingPagesInBackground(targetStatus);
          } else {
            set({
              [statusState.fullyLoadedKey]: true,
            } as Partial<BookingsState>);
          }
        };

        try {
          if (!status) {
            await Promise.all([
              fetchStatusPageOne('Pending'),
              fetchStatusPageOne('Approved'),
              fetchStatusPageOne('Rejected'),
            ]);
          } else {
            await fetchStatusPageOne(status);
          }

          set({
            hasLoadedOnce: true,
            isLoading: false,
            isRefetching: false,
          });
        } catch (err: unknown) {
          const message =
            err instanceof Error ? err.message : 'Failed to load registrations';
          set({
            error: message,
            isLoading: false,
            isRefetching: false,
          });
        } finally {
          set({ isFetching: false });
        }
      },
    }),
    {
      name: 'pd-approvals-bookings',
      version: 2,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        organisationId: state.organisationId,
        pending: state.pending,
        approved: state.approved,
        rejected: state.rejected,
        pendingFullyLoaded: state.pendingFullyLoaded,
        approvedFullyLoaded: state.approvedFullyLoaded,
        rejectedFullyLoaded: state.rejectedFullyLoaded,
        pendingTotalCount: state.pendingTotalCount,
        approvedTotalCount: state.approvedTotalCount,
        rejectedTotalCount: state.rejectedTotalCount,
        lastSyncedAt: state.lastSyncedAt,
        hasLoadedOnce: state.hasLoadedOnce,
      }),
      merge: (persisted, current) => {
        const persistedState = persisted as Partial<BookingsState>;
        return {
          ...current,
          ...persistedState,
          isLoading: false,
          isRefetching: false,
          error: null,
          isFetching: false,
          pendingBackgroundInFlight: false,
          approvedBackgroundInFlight: false,
          rejectedBackgroundInFlight: false,
        };
      },
    }
  )
);

export const resetBookingsStore = (): void => {
  useBookingsStore.getState().reset();
  useBookingsStore.persist.clearStorage();
};

export const seedBookingsStore = (partial: Partial<BookingsState>): void => {
  useBookingsStore.setState({
    ...initialState,
    hasLoadedOnce: true,
    ...partial,
  });
};

export const setBookingsOrganisationId = (
  organisationId: string | undefined
): void => {
  const store = useBookingsStore.getState();

  if (!organisationId) {
    store.reset();
    return;
  }

  if (store.organisationId && store.organisationId !== organisationId) {
    store.reset();
  }

  useBookingsStore.setState({ organisationId });
};
